type TOrder = 'asc' | 'desc';
type TOrderByIteratee<T> = ((value: T) => unknown) | keyof T;
type TMany<T> = T | T[];

const normalizeMany = <T>(value?: TMany<T>): T[] => {
  if (value === undefined) {
    return [];
  }

  return Array.isArray(value) ? value : [value];
};

const getIterateeValue = <T>(
  item: T,
  iteratee: TOrderByIteratee<T>
): unknown => {
  if (typeof iteratee === 'function') {
    return iteratee(item);
  }

  return item?.[iteratee];
};

const compareValues = (left: unknown, right: unknown): number => {
  if (left === right) {
    return 0;
  }

  if (left === undefined) {
    return 1;
  }

  if (right === undefined) {
    return -1;
  }

  if (left === null) {
    return 1;
  }

  if (right === null) {
    return -1;
  }

  if (left > right) {
    return 1;
  }

  if (left < right) {
    return -1;
  }

  return 0;
};

export const orderBy = <T>(
  collection: T[] | undefined,
  iteratees?: TMany<TOrderByIteratee<T>>,
  orders?: TMany<TOrder>
): T[] => {
  if (!collection?.length) {
    return [];
  }

  const normalizedIteratees = normalizeMany(iteratees);
  const normalizedOrders = normalizeMany(orders);

  if (!normalizedIteratees.length) {
    return [...collection];
  }

  return [...collection].sort((leftItem, rightItem) => {
    for (const [index, iteratee] of normalizedIteratees.entries()) {
      const leftValue = getIterateeValue(leftItem, iteratee);
      const rightValue = getIterateeValue(rightItem, iteratee);
      const comparison = compareValues(leftValue, rightValue);

      if (comparison !== 0) {
        return normalizedOrders[index] === 'desc'
          ? comparison * -1
          : comparison;
      }
    }

    return 0;
  });
};
